import { useReducer } from 'react';

import { pdfDownload } from '@hydra/utils/pdfDownload';
import { useUserContext } from '@hydra/common/context/user';

export const initialDownloadPdfState = {
  busy: false,
  error: false
};

const ON_DOWNLOAD_PDF_START = 'ON_DOWNLOAD_PDF_START';
const ON_DOWNLOAD_PDF_SUCCESS = 'ON_DOWNLOAD_PDF_SUCCESS';
const ON_DOWNLOAD_PDF_ERROR = 'ON_DOWNLOAD_PDF_ERROR';
const ON_DOWNLOAD_PDF_ERROR_USER_UNALLOWED = 'ON_DOWNLOAD_PDF_ERROR_USER_UNALLOWED';

interface DownloadPdfReducerState {
  busy: boolean
  error: boolean
  success?: boolean
  errorMessage?: string
}

interface DownloadPdfReducerAction {
  type: 'ON_DOWNLOAD_PDF_START' | 'ON_DOWNLOAD_PDF_SUCCESS' | 'ON_DOWNLOAD_PDF_ERROR' | 'ON_DOWNLOAD_PDF_ERROR_USER_UNALLOWED'
}

export const downloadPdfReducer = (state: DownloadPdfReducerState, action: DownloadPdfReducerAction) => {
  switch (action.type) {
    case ON_DOWNLOAD_PDF_START:
      return {
        ...state,
        busy: true,
        error: false
      };
    case ON_DOWNLOAD_PDF_ERROR:
      return {
        ...state,
        busy: false,
        error: true,
        success: false,
        errorMessage: 'error'
      };
    case ON_DOWNLOAD_PDF_ERROR_USER_UNALLOWED:
      return {
        ...state,
        busy: false,
        error: true,
        success: false,
        errorMessage: 'unallowed'
      };
    case ON_DOWNLOAD_PDF_SUCCESS:
      return {
        ...state,
        busy: false,
        error: false,
        success: true
      };
    default:
      return state;
  }
};

export function usePdfDownload ({ url }: { url: string }) {
  const [state, dispatch] = useReducer(downloadPdfReducer, initialDownloadPdfState);
  const { user } = useUserContext();
  const isPdfAllowed = user && user.isPdfAllowed;
  const pdfEditorLimited = !isPdfAllowed;

  async function downloadPdf ({ templateId }: { templateId?: string } = {}) {
    dispatch({ type: 'ON_DOWNLOAD_PDF_START' });
    try {
      const downloadStatus = await pdfDownload({ url, templateId, pdfEditorLimited });
      if (!downloadStatus) {
        dispatch({ type: 'ON_DOWNLOAD_PDF_ERROR' });
      } else {
        dispatch({ type: 'ON_DOWNLOAD_PDF_SUCCESS' });
      }
    } catch (error) {
      dispatch({ type: 'ON_DOWNLOAD_PDF_ERROR' });
      throw error;
    }
  }

  return {
    busy: state.busy,
    error: state.error,
    errorMessage: state.errorMessage,
    downloadPdf
  };
}

export default usePdfDownload;
